// eslint-disable-next-line max-classes-per-file
import React, {
    DependencyList,
    ReactNode,
    useEffect, useMemo, useRef, useState,
} from "react";

import { RiAlertFill, RiSaveLine } from "@remixicon/react";
import { createBrowserHistory } from "history";
import { createDeepEqual, DeepComparer, isDeepEqual } from "../../api";
import { LoggingHandler } from "../../contexts/logging/LoggingContext";
import EventHandler from "../../event-handler";
import SavePrompt from "../save-prompt/SavePrompt";
import TimedMessage from "../timed-message/TimedMessage";

export const BrowserHistory = createBrowserHistory();

type OnSaved = () => void;

export type DefaultObject = {[key: string]: unknown};

type SetValue<T> = (value: T | ((param: T) => T)) => void

enum Events {
    UPDATED = "UPDATED",
    RESET = "RESET"
}
export type SaveHandler<TObject extends DefaultObject> = (event: FormTracker<TObject>, callback: OnSaved) => Promise<void> | void;

export type Comparer<TValue> = (left: TValue | undefined, right: TValue | undefined) => boolean | undefined;

function isSameReference<T>(left: T, right: T) {
    return left === right;
}

function serialize<TObject extends DefaultObject>(properties: Record<string, Property<TObject[string]>> | undefined) {
    const obj: TObject = Object.create(null);
    Object.entries(properties ?? {}).forEach(([key, value]) => {
        const converted = (value.value as {serialize?: () => TObject});
        obj[key as keyof TObject] = (converted?.serialize?.() ?? value.value) as TObject[string];
    });
    return obj;
}

export class Property<TValue> {
    protected _value: TValue | undefined;

    protected defaultValue: TValue | undefined;

    public setValue: SetValue<TValue>;

    public setCheckedFromInput: (event: React.ChangeEvent<HTMLInputElement>) => void;

    public setValueFromInput: (event: React.ChangeEvent<HTMLInputElement>) => void;

    constructor(
        onChange: (property: Property<TValue>) => void,
        protected create: () => TValue,
        private isEqual: Comparer<TValue> = isSameReference,
    ) {
        this.setValue = ((value: TValue | ((param: TValue) => TValue)) => {
            this._value = value instanceof Function ? value(this._value as TValue) : value;
            onChange(this);
        }) as SetValue<TValue>;

        this.setCheckedFromInput = (event: React.ChangeEvent<HTMLInputElement>) => {
            this.setValue(event.target.checked as TValue);
        };

        this.setValueFromInput = (event: React.ChangeEvent<HTMLInputElement>) => {
            this.setValue(event.target.value as TValue);
        };
    }

    public get value(): TValue {
        return this._value as TValue;
    }

    public get cleanValue(): TValue {
        return this.defaultValue as TValue;
    }

    public get isDirty(): boolean {
        return !this.isEqual(this.value, this.defaultValue);
    }

    reset() {
        this.defaultValue = this.create();
        this._value = this.defaultValue;
    }

    snapshot() {
        this.defaultValue = this._value;
    }

    public serialize(): TValue | undefined {
        return this.value;
    }
}

export class ProxyProperty<TValue> extends Property<TValue> {
    private properties: Record<string, Property<unknown>> = Object.create(null);

    constructor(
        private events: EventHandler<Events>,
        onChange: (property: Property<TValue>) => void,
        create: () => TValue,
        isEqual: Comparer<TValue> = isSameReference,
    ) {
        super(onChange, create, isEqual);

        this.setValue = (value: TValue | ((param: TValue) => TValue)) => {
            const newValue = value instanceof Function ? value(this.value as TValue) : value;
            this.defaultValue = newValue;
            Object.keys(this.properties).forEach(key => {
                const propertyValue = newValue?.[key as keyof TValue];
                if (propertyValue instanceof Property) {
                    this.properties[key].setValue(propertyValue.value);
                } else if (propertyValue) {
                    this.properties[key].setValue(propertyValue);
                }
            });
            onChange(this);
            // parentSet(this.value);
        };
    }

    public get value() {
        const obj: TValue = Object.create(null);
        Object.entries(this.properties).forEach(([key, value]) => {
            obj[key as keyof TValue] = value.value as TValue[keyof TValue];
        });
        return obj;
    }

    public useBuilder<T, TResult>(
        handler: (builder: FormTrackerBuilder<T, [TValue]>) => FormTrackerBuilder<TResult, [TValue]>,
    ): TResult {
        this.events.useEvent(Events.UPDATED);

        // eslint-disable-next-line react-hooks/rules-of-hooks
        const props = useMemo(() => {
            const base: Record<string, Property<unknown>> = Object.create(null);
            // eslint-disable-next-line @typescript-eslint/no-use-before-define
            const builder = new FormTrackerBuilder<TResult, [TValue]>(this.events, base, this);
            handler(builder);

            Object.values(base).forEach(property => {
                property.reset();
            });

            return base as PropertyObject<TResult>;
        }, [this]);

        this.properties = {
            ...this.properties,
            ...props,
        };

        return this.properties as unknown as TResult;
    }

    // eslint-disable-next-line class-methods-use-this
    public override reset() {
        this.defaultValue = this.create();
        this._value = this.defaultValue;
        Object.values(this.properties).forEach(property => {
            property.reset();
        });
    }

    public get isDirty(): boolean {
        return Object.values(this.properties ?? {}).some(prop => prop.isDirty);
    }

    snapshot() {
        this.defaultValue = this._value;
        Object.values(this.properties).forEach(property => {
            property.snapshot();
        });
    }

    public serialize(): TValue | undefined {
        const entries = Object.entries(this.properties ?? {});
        if (this.value == null && entries.isEmpty) {
            return undefined;
        }
        const obj: TValue = Object.create(null);
        entries.forEach(([key, value]) => {
            obj[key as keyof TValue] = value.serialize() as TValue[keyof TValue];
        });
        return obj;
    }
}

type PropertyObject<T> = {
    [P in keyof T]: Property<T[P]>;
};

export class FormTrackerBuilder<TBase, TParams extends unknown[] | never = never> {
    private base: Record<string, Property<unknown>>;

    constructor(
        private events: EventHandler<Events>,
        base?: Record<string, Property<unknown>>,
        private initalValue?: Property<TParams[0]>,
    ) {
        this.base = base ?? Object.create(null);
    }

    public object<TKey extends string, TValue>(
        key: TKey,
        create: (...args: TParams) => TValue,
        comparer?: DeepComparer,
    ) {
        return this.property(key, create, comparer ? createDeepEqual(comparer) : isDeepEqual);
    }

    public array<TKey extends string, TValue extends unknown[] | undefined | null>(
        key: TKey,
        create: (...args: TParams) => TValue,
    ) {
        return this.property(key, create, (left, right) => left === right || (left && right && left?.equals(right)));
    }

    public unsortedArray<TKey extends string, TValue extends unknown[] | undefined>(
        key: TKey,
        create: (...args: TParams) => TValue,
    ) {
        return this.property(
            key,
            create,
            (left, right) => left === right
            || (left && right && left.length === right.length && left.union(right).length === left.length),
        );
    }

    public property<TKey extends string, TValue>(
        key: TKey,
        create: (...args: TParams) => TValue,
        isEqual?: Comparer<TValue>,
    ): FormTrackerBuilder<TBase & { [T in TKey]: Property<TValue> }, TParams> {
        const property = new Property<TValue>(() => {
            this.events.emit(Events.UPDATED);
        }, () => create(...([this.initalValue?.cleanValue]) as TParams), isEqual);
        this.base[key] = property as Property<unknown>;
        return this as FormTrackerBuilder<TBase & {[T in TKey]: TValue }, TParams>;
    }

    public child<TKey extends string, TValue, TNext>(
        key: TKey,
        create: (builder: FormTrackerBuilder<TValue, TParams>) => FormTrackerBuilder<TNext, TParams>,
    ): FormTrackerBuilder<TBase & { [T in TKey]: PropertyObject<TNext> }, TParams> {
        const createProperty = () => {
            const base: Record<string, Property<unknown>> = Object.create(null);
            const builder = new FormTrackerBuilder<TValue, TParams>(this.events, base, this.initalValue);
            create(builder);
            Object.values(base).forEach(property => {
                property.reset();
            });

            const baseWithSerializer = base as (typeof base & {
                serialize: () => TNext
            });
            baseWithSerializer.serialize = () => serialize(base) as TNext;

            return base as TNext;
        };

        const property = new ProxyProperty<TNext>(this.events, () => {
            this.events.emit(Events.UPDATED);
        }, createProperty);

        this.base[key] = property as Property<unknown>;
        return this as FormTrackerBuilder<TBase & { [T in TKey]: PropertyObject<TNext> }, TParams>;
    }

    public proxy<TKey extends string, TValue>(
        key: TKey,
        create: (...args: TParams) => TValue,
        comparer?: DeepComparer,
    ): FormTrackerBuilder<TBase & { [T in TKey]: ProxyProperty<TValue> }, TParams> {
        const property = new ProxyProperty<TValue>(this.events, () => {
            this.events.emit(Events.UPDATED);
        }, () => create(...([this.initalValue?.cleanValue]) as TParams), comparer ? createDeepEqual(comparer) : isDeepEqual);

        this.base[key] = property as Property<unknown>;
        return this as FormTrackerBuilder<TBase & { [T in TKey]: PropertyObject<TValue> }, TParams>;
    }

    public build(): FormTrackerBuilder<{[T in keyof TBase]: Property<TBase[T]> }, TParams> {
        return this;
    }
}

type ObjectConverter<T extends DefaultObject> = {
    [P in keyof T]: T[P] extends Property<infer TValue> ? TValue : T[P];
}

export class FormTracker<TObject extends DefaultObject> {
    private events = new EventHandler<Events>();

    private _properties: Record<string, Property<TObject[string]>> | undefined;

    public get properties(): TObject {
        if (!this._properties) {
            throw new Error("Properties have not been initialized");
        }
        return this._properties as unknown as TObject;
    }

    public serialize(): ObjectConverter<TObject> {
        const obj: TObject = Object.create(null);
        Object.entries(this._properties ?? {}).forEach(([key, value]) => {
            obj[key as keyof TObject] = value.serialize() as TObject[keyof TObject];
        });
        return obj as ObjectConverter<TObject>;
    }

    public reset() {
        this.events.emit(Events.UPDATED);
    }

    public setValue(value: ObjectConverter<TObject>) {
        if (!this._properties) {
            return;
        }
        Object.entries(this._properties).forEach(([key, property]) => {
            property.setValue?.(value[key as keyof TObject] as TObject[string]);
        });
        this.events.emit(Events.UPDATED);
    }

    public snapshot() {
        if (!this._properties) {
            return;
        }
        Object.values(this._properties).forEach(property => property.snapshot?.());
        this.events.emit(Events.UPDATED);
    }

    public resetDefault() {
        if (!this._properties) {
            return;
        }
        Object.values(this._properties).forEach(property => property.reset());
        this.events.emit(Events.UPDATED);
    }

    public useOnChange(callback: () => void, dependencies?: DependencyList) {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        useEffect(() => {
            this.events.on(Events.UPDATED, callback);
            return () => {
                this.events.off(Events.UPDATED, callback);
            };
        }, [...dependencies ?? [], this]);
    }

    useBuilder<T, TResult>(
        handler: (builder: FormTrackerBuilder<T>) => FormTrackerBuilder<TResult>,
        dependencies: DependencyList,
    ): TResult {
        this.events.useEvent(Events.UPDATED);

        // eslint-disable-next-line react-hooks/rules-of-hooks
        const props = useMemo(() => {
            const base: Record<string, Property<unknown>> = Object.create(null);
            const builder = new FormTrackerBuilder<TResult>(this.events, base);
            handler(builder);

            Object.values(base).forEach(property => {
                property.reset();
            });

            return base as PropertyObject<TResult>;
        }, [...dependencies]);

        this._properties = {
            ...this._properties,
            ...props,
        };

        // eslint-disable-next-line react-hooks/rules-of-hooks
        useEffect(() => {
            this._properties = {
                ...this._properties,
                ...props,
            };
        }, [props]);

        // eslint-disable-next-line react-hooks/rules-of-hooks
        useEffect(() => {
            this.events.emit(Events.UPDATED);
        }, [...dependencies]);

        return this._properties as unknown as TResult;
    }

    public get isDirty() {
        return this._properties ? Object.values(this._properties ?? {}).some(prop => prop.isDirty) : false;
    }

    useDirtyTracker({
        onSave,
        finishedSavingMessage,
        saveText,
        isDirty,
        buttonText,
        canReset,
        actions,
    }: {
        onSave: SaveHandler<TObject>;
        finishedSavingMessage?: string;
        saveText?: string;
        isDirty?: boolean;
        buttonText?: ReactNode;
        canReset?: boolean;
        actions?: ReactNode;
    }) {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const [isShowSaved, setShowSaved] = useState(false);

        // eslint-disable-next-line react-hooks/rules-of-hooks
        const [saveError, setSaveError] = useState<string | undefined>();

        // eslint-disable-next-line react-hooks/rules-of-hooks
        const [isSaving, setIsSaving] = useState(false);

        return {
            element: (
                <>
                    {finishedSavingMessage && (
                        <TimedMessage
                            shouldShow={isShowSaved}
                            prefixIcon={<RiSaveLine />}
                            onClose={() => setShowSaved(false)}
                        >
                            {finishedSavingMessage}
                        </TimedMessage>
                    )}
                    {saveError && (
                        <TimedMessage
                            shouldShow={saveError != null}
                            prefixIcon={<RiAlertFill />}
                            onClose={() => setSaveError(undefined)}
                            variant="danger"
                            title="Error occurred."
                        >
                            {saveError}
                        </TimedMessage>
                    )}
                    <SavePrompt
                        onSave={evt => {
                            evt.preventDefault();
                            LoggingHandler.instance.wrap(async () => {
                                try {
                                    setIsSaving(true);
                                    await onSave(this, () => {
                                        setIsSaving(false);
                                        this.snapshot();
                                        if (finishedSavingMessage) {
                                            setShowSaved(true);
                                        }
                                    });
                                } catch (err) {
                                    setIsSaving(false);
                                    setSaveError((err as Error).message);
                                }
                            });
                        }}
                        onReset={canReset !== false ? () => {
                            this.resetDefault();
                        } : undefined}
                        text={saveText}
                        buttonText={buttonText}
                        actions={actions}
                        shouldShow={isDirty ?? (this.isDirty && !isSaving)}
                    />
                </>
            ),
        };
    }
}

export function useFormTracker<TObject extends DefaultObject>(
    dependencies?: React.DependencyList,
) {
    return useMemo(() => new FormTracker<TObject>(), dependencies ?? []);
}

export function useFormBuilder<T, TResult extends DefaultObject>(
    handler: (builder: FormTrackerBuilder<T>) => FormTrackerBuilder<TResult>,
    dependencies: DependencyList,
) {
    const tracker = useFormTracker<TResult>([]);
    const properties = tracker.useBuilder<T, TResult>(handler, dependencies);
    return { tracker, properties };
}

export function useProxyProperty<T>(
    handler: () => T,
    dependencies?: DependencyList,
    comparer?: DeepComparer,
): ProxyProperty<T> {
    const tracker = useFormTracker<DefaultObject>([]);
    const properties = tracker.useBuilder(builder => builder.proxy("proxy", handler, comparer), dependencies ?? []);
    return properties.proxy;
}

export function useDependencyComparison<T>(lastValue: T, isEqual: Comparer<T> = isSameReference): number {
    const ref = useRef({
        lastValue,
        counter: 0,
    });

    if (isEqual(ref.current.lastValue, lastValue)) {
        return ref.current.counter;
    }
    ref.current.lastValue = lastValue;
    ref.current.counter += 1;
    return ref.current.counter;
}
